import { Store, ProductionStatus, visionService } from '../database/vision';

export const visionChatCommands = {
  // Comando para obter status geral
  async getStatus() {
    try {
      const metrics = await visionService.getMetrics();
      const { total, completed, inProgress, notStarted } = metrics;

      // Determina o tom da resposta baseado nas métricas
      let tone = '';
      const completionRate = (completed / total) * 100;
      
      if (completionRate >= 80) {
        tone = 'Excelente progresso! ';
      } else if (completionRate >= 50) {
        tone = 'Estamos avançando bem. ';
      } else if (inProgress > notStarted) {
        tone = 'Temos bastante trabalho em andamento. ';
      } else {
        tone = 'Precisamos focar em iniciar mais projetos. ';
      }

      return `${tone}Atualmente estamos gerenciando ${total} ${total === 1 ? 'loja' : 'lojas'}, sendo:
• ${completed} ${completed === 1 ? 'projeto concluído' : 'projetos concluídos'} ✨
• ${inProgress} em desenvolvimento 🔄
• ${notStarted} ${notStarted === 1 ? 'aguardando início' : 'aguardando início'} ⏳

${completed === total ? 'Parabéns! Todos os projetos estão concluídos!' : 
  `A taxa de conclusão está em ${completionRate.toFixed(1)}%.`}`;
    } catch (error) {
      return 'Desculpe, tive um problema ao acessar as informações das lojas. Pode tentar novamente?';
    }
  },

  // Comando para buscar loja específica
  async getStore(name: string) {
    try {
      const stores = await visionService.getStores();
      const store = stores.find(s => 
        s.name.toLowerCase().includes(name.toLowerCase())
      );

      if (!store) {
        return `Não encontrei nenhuma loja chamada "${name}". Tem certeza que digitou o nome corretamente?`;
      }

      const statusEmojis = {
        done: '✅',
        in_progress: '🔄',
        not_started: '⏳'
      };

      const statusTexts = {
        done: 'concluído',
        in_progress: 'em andamento',
        not_started: 'não iniciado'
      };

      const deadline = new Date(store.deadline);
      const today = new Date();
      const daysLeft = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      let deadlineText = '';
      if (daysLeft < 0) {
        deadlineText = `Projeto está atrasado em ${Math.abs(daysLeft)} dias`;
      } else if (daysLeft === 0) {
        deadlineText = 'Prazo é hoje!';
      } else {
        deadlineText = `${daysLeft} dias restantes`;
      }

      const completedTasks = Object.values(store.status).filter(s => s === 'done').length;

      return `Sobre a ${store.name}:

${store.plan.charAt(0).toUpperCase() + store.plan.slice(1)} • ${deadlineText}

Progresso do projeto:
• ID Visual: ${statusEmojis[store.status.idVisual]} ${statusTexts[store.status.idVisual]}
• Banners: ${statusEmojis[store.status.banners]} ${statusTexts[store.status.banners]}
• Social Media: ${statusEmojis[store.status.sm]} ${statusTexts[store.status.sm]}
• Motion: ${statusEmojis[store.status.motion]} ${statusTexts[store.status.motion]}

${completedTasks === 4 ? '🎉 Projeto concluído!' : 
  `Progresso: ${completedTasks}/4 etapas concluídas`}

Equipe: ${store.team_members.map(m => m.name).join(', ')}`;
    } catch (error) {
      return 'Ops, tive um problema ao buscar as informações dessa loja. Pode tentar novamente?';
    }
  },

  // Comando para listar lojas atrasadas
  async getOverdueStores() {
    try {
      const stores = await visionService.getStores();
      const now = new Date();
      const overdue = stores.filter(store => 
        new Date(store.deadline) < now && 
        Object.values(store.status).some(status => status !== 'done')
      );

      if (overdue.length === 0) {
        return '🎉 Ótimas notícias! Não temos nenhum projeto atrasado no momento. Continue mantendo esse excelente controle!';
      }

      const overdueText = overdue.map(store => {
        const daysLate = Math.ceil((now.getTime() - new Date(store.deadline).getTime()) / (1000 * 60 * 60 * 24));
        const pendingTasks = Object.entries(store.status)
          .filter(([_, status]) => status !== 'done')
          .map(([key]) => {
            switch(key) {
              case 'idVisual': return 'ID Visual';
              case 'banners': return 'Banners';
              case 'sm': return 'Social Media';
              case 'motion': return 'Motion';
              default: return key;
            }
          });

        return `• ${store.name} (${daysLate} ${daysLate === 1 ? 'dia' : 'dias'} de atraso)
  Pendente: ${pendingTasks.join(', ')}`;
      }).join('\n\n');

      return `⚠️ Encontrei ${overdue.length} ${overdue.length === 1 ? 'projeto atrasado' : 'projetos atrasados'}:

${overdueText}

Recomendo priorizar esses projetos para minimizar os atrasos.`;
    } catch (error) {
      return 'Desculpe, tive um problema ao verificar os projetos atrasados. Pode tentar novamente?';
    }
  },

  // Comando para listar próximos prazos
  async getUpcomingDeadlines() {
    try {
      const stores = await visionService.getStores();
      const now = new Date();
      const threeDaysFromNow = new Date(now.getTime() + (3 * 24 * 60 * 60 * 1000));
      
      const upcoming = stores.filter(store => {
        const deadline = new Date(store.deadline);
        return deadline > now && deadline <= threeDaysFromNow;
      });

      if (upcoming.length === 0) {
        return '😌 Não há prazos urgentes nos próximos 3 dias. Aproveite para focar nos projetos em andamento!';
      }

      const upcomingText = upcoming.map(store => {
        const daysLeft = Math.ceil((new Date(store.deadline).getTime() - now.getTime()) / (1000 * 60 * 60 * 1000 * 24));
        const completedTasks = Object.values(store.status).filter(s => s === 'done').length;
        const progress = (completedTasks / 4) * 100;

        return `• ${store.name}
  Prazo: ${daysLeft === 1 ? 'amanhã' : `em ${daysLeft} dias`}
  Progresso: ${progress.toFixed(0)}% concluído`;
      }).join('\n\n');

      return `⏰ Atenção aos próximos prazos:

${upcomingText}

Mantenha o foco nesses projetos para garantir as entregas no prazo!`;
    } catch (error) {
      return 'Desculpe, tive um problema ao verificar os próximos prazos. Pode tentar novamente?';
    }
  }
};
